import {
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMapPin } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatAnswerCaptureMetadataLines, getAnswerCaptureCoordinates } from '../../utils/format-answer-capture-metadata';
import { formatPhotoExifSummary } from '../../utils/format-photo-exif';
import { parseHarvestProofApiError } from '../../utils/parse-api-error';
import { AnswerCaptureMapModal } from '../answer-capture-map-modal/answer-capture-map-modal';
import { HarvestProofFarmerLink } from '../harvest-proof-farmer-link/harvest-proof-farmer-link';
import { HarvestProofRemindersStatus } from '../harvest-proof-reminders-status/harvest-proof-reminders-status';
import { HarvestProofStatusBadge } from '../harvest-proof-status-badge/harvest-proof-status-badge';

import classes from './harvest-proof-detail-panel.module.css';

import { useAuth } from '@auth/data-access';
import { formatDate, hasAdminAccess } from '@data-access';
import {
  harvestProofApi,
  harvestProofQueries,
  HarvestProofFormStatus,
  isHarvestProofAnswerSubmitted,
} from '@harvest-proof/data-access';

interface HarvestProofDetailPanelProps {
  formId: number;
}

export const HarvestProofDetailPanel: FC<HarvestProofDetailPanelProps> = ({
  formId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const detailQuery = useQuery(harvestProofQueries.detail(formId));
  const canManageHarvestProof = hasAdminAccess(user?.user_access);
  const [previewAnswerId, setPreviewAnswerId] = useState<number | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: harvestProofQueries.all });
  };

  const forceSendSms = useMutation({
    mutationFn: harvestProofApi.forceSendSms,
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: t('harvest-proof.notifications.sent'),
      });
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: parseHarvestProofApiError(error),
      });
    },
  });

  const resetForm = useMutation({
    mutationFn: (id: number) => harvestProofApi.reset(id),
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: t('harvest-proof.notifications.reset'),
      });
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: parseHarvestProofApiError(error),
      });
    },
  });

  const previewAnswer = useMemo(() => {
    if (!detailQuery.data || previewAnswerId == null) {
      return null;
    }

    return (
      detailQuery.data.answers.find((answer) => answer.id === previewAnswerId) ??
      null
    );
  }, [detailQuery.data, previewAnswerId]);

  if (detailQuery.isLoading) {
    return (
      <Box className={classes.root}>
        <Loader size="sm" />
      </Box>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Box className={classes.root}>
        <Text c="red" size="sm">
          {t('harvest-proof.detail.loadError')}
        </Text>
      </Box>
    );
  }

  const form = detailQuery.data;
  const hasPhone = Boolean(form.account_phone?.trim());
  const canForceSendSms =
    hasPhone && form.status !== HarvestProofFormStatus.COMPLETE;
  const forceSendSmsTooltip = !hasPhone
    ? form.is_tc_sms
      ? t('harvest-proof.actions.noTcPhone')
      : t('harvest-proof.actions.noPhone')
    : form.status === HarvestProofFormStatus.COMPLETE
      ? t('harvest-proof.actions.formComplete')
      : t('harvest-proof.actions.forceSendSmsHint');

  return (
    <Box className={classes.root}>
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <div>
            <Group gap="sm" mb={4}>
              <Text fw={700}>{form.account_name}</Text>
              <HarvestProofStatusBadge status={form.status} variant="form" />
            </Group>
            <Text c="dimmed" size="sm">
              {form.is_tc_sms
                ? t('harvest-proof.detail.tcPhone', { phone: form.account_phone })
                : form.account_phone}
            </Text>
          </div>
          {canManageHarvestProof ? (
            <Group gap="xs" wrap="wrap">
              <Tooltip label={forceSendSmsTooltip} withArrow>
                <span>
                  <Button
                    disabled={!canForceSendSms}
                    loading={forceSendSms.isPending}
                    onClick={() => forceSendSms.mutate(form.id)}
                    size="sm"
                    variant="light"
                  >
                    {t('harvest-proof.actions.forceSendSms')}
                  </Button>
                </span>
              </Tooltip>
              <Button
                color="orange"
                loading={resetForm.isPending}
                onClick={() => resetForm.mutate(form.id)}
                size="sm"
                variant="light"
              >
                {t('harvest-proof.actions.reset')}
              </Button>
            </Group>
          ) : null}
        </Group>

        <HarvestProofFarmerLink
          temporaryLink={form.temporary_link}
          token={form.token}
        />

        <Stack gap={4}>
          <Text fw={600} size="sm">
            {t('harvest-proof.reminders.title')}
          </Text>
          <Text c="dimmed" size="xs">
            {t('harvest-proof.reminders.description')}
          </Text>
          <HarvestProofRemindersStatus form={form} size="md" />
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
          {form.answers.map((answer) => {
            const isSubmitted = isHarvestProofAnswerSubmitted(answer);
            const captureMetadataLines = isSubmitted
              ? formatAnswerCaptureMetadataLines(
                  answer,
                  {
                    receivedAt: t('harvest-proof.detail.receivedAt'),
                    gps: t('harvest-proof.detail.captureGps'),
                    heading: t('harvest-proof.detail.heading'),
                    unavailable: t('harvest-proof.detail.unavailable'),
                  },
                  (iso) =>
                    formatDate({
                      date: iso,
                      format: 'display-hour',
                    }),
                )
              : [];
            const exifSummary = formatPhotoExifSummary(answer.photo_exif);
            const captureCoordinates = isSubmitted
              ? getAnswerCaptureCoordinates(answer)
              : null;

            return (
              <Paper key={answer.id} p="sm" radius="md" withBorder>
                <Group align="flex-start" justify="space-between" wrap="nowrap">
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text fw={600} lineClamp={1} style={{ flex: 1, minWidth: 0 }}>
                        {answer.field.properties?.name ?? answer.field.id}
                      </Text>
                      {captureCoordinates ? (
                        <Tooltip
                          label={t('harvest-proof.detail.viewOnMap')}
                          withArrow
                        >
                          <ActionIcon
                            aria-label={t('harvest-proof.detail.viewOnMap')}
                            onClick={() => setPreviewAnswerId(answer.id)}
                            size="sm"
                            variant="subtle"
                          >
                            <IconMapPin size={16} stroke={1.75} />
                          </ActionIcon>
                        </Tooltip>
                      ) : null}
                    </Group>
                    {!isSubmitted ? (
                      <Text c="dimmed" size="xs">
                        {t('harvest-proof.detail.pending')}
                      </Text>
                    ) : null}
                    {answer.distance_meters != null ? (
                      <Text size="xs">
                        {t('harvest-proof.detail.distance')}:{' '}
                        {Math.round(answer.distance_meters)} m
                      </Text>
                    ) : null}
                    {captureMetadataLines.map((line) => (
                      <Text key={line} size="xs">
                        {line}
                      </Text>
                    ))}
                    {exifSummary ? (
                      <Text lineClamp={3} size="xs">
                        {exifSummary}
                      </Text>
                    ) : null}
                  </Stack>
                  <HarvestProofStatusBadge
                    status={answer.status}
                    variant="parcel"
                  />
                </Group>
                {answer.photo ? (
                  <img
                    alt="Preuve"
                    className={classes.proofImage}
                    src={answer.photo}
                  />
                ) : null}
              </Paper>
            );
          })}
        </SimpleGrid>

        <AnswerCaptureMapModal
          answer={previewAnswer}
          onClose={() => setPreviewAnswerId(null)}
          opened={previewAnswerId != null}
        />
      </Stack>
    </Box>
  );
};
